import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Terminal as TerminalIcon, Folder, File, FileCode, Upload, Play, Loader2, RefreshCw, Server, Settings, Globe, Code, LayoutTemplate, SquareChartGantt, LoaderCircle, Check, ArrowLeft, Save, Bot, Send, AlertTriangle, Clock, Square, ChevronDown, Lock, Edit3, Maximize2, Share, MoreHorizontal, Mic, Plus, Search, MousePointer2, ArrowUp, Menu, FileText, Sparkles, Download, Paperclip, Lightbulb, Eye, ChevronRight } from 'lucide-react';
import { Terminal } from 'xterm';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FitAddon } from 'xterm-addon-fit';
import { WebLinksAddon } from 'xterm-addon-web-links';
import 'xterm/css/xterm.css';
import api from '../services/api';
import '../styles/workspace.css';

// Join the container base directory with a path relative to it.
const joinPath = (base, relative) => {
  const cleanBase = (base || '/').replace(/\/+$/, '');
  if (!relative) return cleanBase || '/';
  return `${cleanBase}/${relative}`;
};

// Drop the last segment of a relative path ('' means we are at the project root).
const parentDir = (relative) => {
  if (!relative) return '';
  const parts = relative.split('/').filter(Boolean);
  parts.pop();
  return parts.join('/');
};

const formatElapsed = (ms) => {
  const totalSec = Math.max(0, Math.floor(Number(ms) / 1000) || 0);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

const ComposeWorkspaceModal = ({ appName, owner, sshInfo, deploymentSession, deploymentType = 'compose', initialTab = 'ai', onClose, onMinimize, onRestore, onCancel, autoDeploy = true, isMinimized = false }) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'ai');
  
  // File state. `currentDir` is relative to the project root inside the container;
  // `baseDir` is the absolute root the backend resolved (e.g. /app or /home/app).
  const [files, setFiles] = useState([]);
  const [currentDir, setCurrentDir] = useState('');
  const [baseDir, setBaseDir] = useState(sshInfo?.basePath || '');
  const [filesError, setFilesError] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(null);
  
  // Editor state
  const [activeFile, setActiveFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [loadingFileContent, setLoadingFileContent] = useState(false);
  const [savingFileContent, setSavingFileContent] = useState(false);

  // Env state
  const [envContent, setEnvContent] = useState('');
  const [loadingEnv, setLoadingEnv] = useState(false);
  const [savingEnv, setSavingEnv] = useState(false);

  // Sync state if props load asynchronously after mount
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    if (sshInfo?.basePath) setBaseDir(sshInfo.basePath);
  }, [sshInfo?.basePath]);

  const [actionLoading, setActionLoading] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const sessionStartedAt = deploymentSession?.createdAt ? new Date(deploymentSession.createdAt).getTime() : null;
  const startedAtRef = useRef(sessionStartedAt || sshInfo?.deployStartedAt || Date.now());
  const finishedAtRef = useRef(null);
  const verifiedRef = useRef(false);
  const onAiVerifiedRef = useRef(sshInfo?.onAiVerified);
  const sshInfoRef = useRef(sshInfo);
  const onCloseRef = useRef(onClose);

  // AI page state
  const [aiInput, setAiInput] = useState('');
  const [agentMode, setAgentMode] = useState('action'); // 'action' | 'ask'
  const [aiChat, setAiChat] = useState(() => {
    const initial = [{
      role: 'system',
      content: autoDeploy
        ? 'Workspace AI is ready. It will inspect the fetched repository, install dependencies, and start the app automatically.'
        : 'Workspace AI is ready. Ask it to inspect logs, fix issues, or restart the app — it will not auto-run on reopen.'
    }];
    if (sshInfo?.setupError) {
      initial.push({ role: 'assistant', content: `⚠️ **Deployment Failed:**\n${sshInfo.setupError}\n\n*The deployment pipeline failed before the Workspace AI could start.*` });
    }
    return initial;
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiThinking, setAiThinking] = useState('');
  const [aiStatus, setAiStatus] = useState('');
  const [aiAutoDone, setAiAutoDone] = useState(false);
  const [runVerification, setRunVerification] = useState(null); // null | { ok, listening, exposed, message, publicUrl }
  const [centerPane, setCenterPane] = useState('activity'); // activity | preview | editor
  const [previewKey, setPreviewKey] = useState(0);

  const terminalRef = useRef(null);
  const xtermRef = useRef(null);
  const wsRef = useRef(null);
  const fitAddonRef = useRef(null);
  const fileInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const thinkingRef = useRef(null);
  const logEndRef = useRef(null);
  const termRetryRef = useRef(0);
  const aiAutoStartedRef = useRef(false);
  const aiBusyRef = useRef(false);
  const aiAbortControllerRef = useRef(null);
  const terminalMode = deploymentType === 'compose' ? 'compose' : 'shell';

  useEffect(() => {
    let mounted = true;
    const fetchChat = async () => {
      if (autoDeploy) return; // Brand new deployment: skip fetching old chat history
      try {
        const res = await api.getWorkspaceChat(appName);
        if (mounted && res.chat && res.chat.length > 0) {
          setAiChat(prev => {
            // Keep the initial system/error messages but append the rest.
            // Or better, just completely replace if the history is significant,
            // except if there's a live error right now.
            // But let's just merge them by concatenating, avoiding duplicates.
            const newChat = [...prev];
            for (const msg of res.chat) {
              // Ignore system messages from history if we already have one
              if (msg.role === 'system' && newChat.some(m => m.role === 'system')) continue;
              newChat.push(msg);
            }
            return newChat;
          });
        }
      } catch (err) {
        console.warn('Failed to fetch workspace chat history:', err);
      }
    };
    fetchChat();
    return () => { mounted = false; };
  }, [appName]);

  // Persist chat changes
  const saveChatTimeoutRef = useRef(null);
  useEffect(() => {
    if (aiChat.length <= 1 && aiChat[0]?.role === 'system') return; // Don't save just the greeting
    if (saveChatTimeoutRef.current) clearTimeout(saveChatTimeoutRef.current);
    saveChatTimeoutRef.current = setTimeout(async () => {
      try {
        await api.saveWorkspaceChat(appName, aiChat);
      } catch (e) {
        console.warn('Failed to persist chat:', e);
      }
    }, 1500); // Debounce saves
  }, [aiChat, appName]);

  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    const sessionStartedAt = deploymentSession?.createdAt ? new Date(deploymentSession.createdAt).getTime() : null;
    if (sessionStartedAt) {
      startedAtRef.current = sessionStartedAt;
    } else if (sshInfo?.deployStartedAt) {
      startedAtRef.current = sshInfo.deployStartedAt;
    }
  }, [sshInfo?.deployStartedAt, deploymentSession?.createdAt]);

  useEffect(() => {
    onAiVerifiedRef.current = sshInfo?.onAiVerified;
    sshInfoRef.current = sshInfo;
  }, [sshInfo]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // New provisioning cycle — allow verification + auto-AI again.
  useEffect(() => {
    if (sshInfo?.isDeploying) {
      verifiedRef.current = false;
      finishedAtRef.current = null;
      aiAutoStartedRef.current = false;
    }
  }, [sshInfo?.isDeploying, appName]);

  // If the deployment pipeline fails mid-flight, show the error in the chat
  useEffect(() => {
    if (sshInfo?.setupError) {
      setAiChat(prev => {
        const hasError = prev.some(m => String(m.content || '').includes('Deployment Failed'));
        if (!hasError) {
          return [...prev, { role: 'assistant', content: `⚠️ **Deployment Failed:**\n${sshInfo.setupError}\n\n*The deployment pipeline failed before the Workspace AI could start.*` }];
        }
        return prev;
      });
    }
  }, [sshInfo?.setupError]);

  useEffect(() => {
    const tick = () => {
      if (finishedAtRef.current) {
        setElapsedMs(finishedAtRef.current - (startedAtRef.current || finishedAtRef.current));
      } else {
        setElapsedMs(Date.now() - (startedAtRef.current || Date.now()));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const finishIfVerified = (verification) => {
    if (!verification || !verification.ok || verification.skipped || verifiedRef.current) return;
    verifiedRef.current = true;
    finishedAtRef.current = Date.now();
    const info = sshInfoRef.current || {};
    const payload = {
      verification,
      domain: info.domain || null,
      url: verification.publicUrl || (info.domain ? `${window.location.protocol}//${info.domain}/` : null),
      publicUrls: verification.publicUrls || [],
      bindings: verification.bindings || [],
      apps: verification.apps || [],
      port: info.port || null,
      internalPort: info.internalPort || null,
      cpus: info.cpus || null,
      memory: info.memory || null,
      image: info.image || null,
      containerName: info.containerId || info.containerName || null,
      result: info
    };
    if (typeof onAiVerifiedRef.current === 'function') {
      try { onAiVerifiedRef.current(payload); } catch (_) {}
      // Auto-close modal after a short delay to reveal the underlying success page
      setTimeout(() => {
        if (typeof onCloseRef.current === 'function') {
          onCloseRef.current({ verified: true });
        }
      }, 2000);
    }
  };

  useEffect(() => {
    if (!sshInfo?.isDeploying) {
      const t = setTimeout(() => initTerminal(), 80);
      return () => clearTimeout(t);
    }
    return () => {
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
        wsRef.current = null;
      }
      if (xtermRef.current) {
        // Clean up resize listener to prevent memory leak
        if (xtermRef.current._resizeHandler) {
          window.removeEventListener('resize', xtermRef.current._resizeHandler);
        }
        try { xtermRef.current.dispose(); } catch (_) {}
        xtermRef.current = null;
      }
    };
  }, [sshInfo?.isDeploying, appName]);

  useEffect(() => {
    if (sshInfo?.isDeploying) return;
    if (activeTab === 'terminal' && fitAddonRef.current) {
      try { fitAddonRef.current.fit(); } catch (_) {}
    }
    if (activeTab === 'files') {
      if (!activeFile) loadFiles();
    } else if (activeTab === 'env') {
      loadEnv();
    }
  }, [activeTab, activeFile, currentDir, sshInfo?.isDeploying]);

  useEffect(() => {
    // Lock page scroll while the fullscreen workspace is open. scrollIntoView on
    // log/chat anchors can otherwise walk up and move the document behind us.
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  useEffect(() => {
    const end = chatEndRef.current;
    if (!end) return;
    const thread = end.closest('.gws-chat-pane');
    if (!thread) return;
    
    // Only auto-scroll if user is already near the bottom (within 50px) or if there's no scrollbar yet
    const isNearBottom = thread.scrollHeight - thread.scrollTop - thread.clientHeight < 50;
    
    if (isNearBottom) {
      thread.scrollTop = thread.scrollHeight;
    }
  }, [aiChat, aiStatus, aiLoading]);

  useEffect(() => {
    if (thinkingRef.current) {
      const thread = thinkingRef.current.closest('.gws-chat-pane');
      if (thread && thread.scrollHeight - thread.scrollTop - thread.clientHeight < 50) {
        thinkingRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest' });
      }
    }
  }, [aiThinking]);

  useEffect(() => {
    if (centerPane !== 'activity') return;
    const consoleEl = logEndRef.current && logEndRef.current.closest('.ws-console');
    if (consoleEl) {
      const isNearBottom = consoleEl.scrollHeight - consoleEl.scrollTop - consoleEl.clientHeight < 50;
      if (isNearBottom) {
        consoleEl.scrollTop = consoleEl.scrollHeight;
      }
    }
  }, [aiChat, aiStatus, centerPane]);

  // When the workspace becomes ready, auto-start AI install/run once —
  // only for first-time deploy flows (autoDeploy), not when reopening.
  useEffect(() => {
    if (!autoDeploy) return;
    if (sshInfo?.isDeploying) return;
    if (aiAutoStartedRef.current) return;
    if (!appName) return;
    setActiveTab('ai');
    const timer = setTimeout(() => {
      if (aiBusyRef.current || aiAutoStartedRef.current) return;
      aiAutoStartedRef.current = true;
      runAiPrompt(
        'Deploy this project: understand what it contains, then install, configure, connect and start every app (backend, frontend, admin panel) and give each one a working URL.',
        { auto: true }
      );
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoDeploy, sshInfo?.isDeploying, appName, sshInfo?.isSSR]);

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return '';
    const num = Number(bytes);
    if (num < 1024) return `${num} B`;
    if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
    return `${(num / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadFiles = async (targetDir = currentDir) => {
    setLoadingFiles(true);
    setFilesError('');
    try {
      const data = await api.listContainerFiles(appName, targetDir, owner);
      if (data.baseDir) setBaseDir(data.baseDir);
      setFiles(data.entries || []);
      if (data.error) setFilesError(data.error);
    } catch (err) {
      console.error(err);
      setFiles([]);
      setFilesError(err.message || 'Failed to list files');
    } finally {
      setLoadingFiles(false);
    }
  };

  const openFileEditor = async (entry) => {
    setActiveFile(entry);
    setLoadingFileContent(true);
    try {
      const data = await api.getFileContent(appName, entry.path, owner);
      setFileContent(data.content !== undefined ? data.content : '');
    } catch (err) {
      alert('Failed to load file: ' + err.message);
      setActiveFile(null);
    } finally {
      setLoadingFileContent(false);
    }
  };

  const saveActiveFile = async () => {
    if (!activeFile) return;
    setSavingFileContent(true);
    try {
      await api.updateFileContent(appName, activeFile.path, fileContent, owner);
      alert(`File "${activeFile.name}" saved successfully.`);
    } catch (err) {
      alert('Failed to save file: ' + err.message);
    } finally {
      setSavingFileContent(false);
    }
  };

  const loadEnv = async () => {
    setLoadingEnv(true);
    try {
      const data = await api.getFileContent(appName, '.env', owner);
      setEnvContent(data.content || '');
    } catch (err) {
      setEnvContent(''); // Doesn't exist or error
    } finally {
      setLoadingEnv(false);
    }
  };

  const saveEnv = async () => {
    setSavingEnv(true);
    try {
      await api.updateFileContent(appName, '.env', envContent, owner);
      alert('Environment variables saved successfully.');
    } catch (err) {
      alert('Failed to save .env file: ' + err.message);
    } finally {
      setSavingEnv(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      await api.controlContainer(appName, action);
      alert(`Successfully triggered: ${action}`);
    } catch (err) {
      alert(`Action failed: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const initTerminal = async (opts = {}) => {
    const force = Boolean(opts.force);
    if (xtermRef.current && !force) return;

    if (force) {
      if (wsRef.current) {
        try { wsRef.current.close(); } catch (_) {}
        wsRef.current = null;
      }
      if (xtermRef.current) {
        try { xtermRef.current.dispose(); } catch (_) {}
        xtermRef.current = null;
      }
    }

    if (!terminalRef.current) return;

    const term = new Terminal({
      cursorBlink: true,
      theme: {
        background: '#0a0a0d',
        foreground: '#e4e4e7',
        cursor: '#38bdf8',
        selectionBackground: 'rgba(99, 102, 241, 0.35)',
        black: '#18181b',
        red: '#f87171',
        green: '#34d399',
        yellow: '#fbbf24',
        blue: '#60a5fa',
        magenta: '#c084fc',
        cyan: '#22d3ee',
        white: '#e4e4e7'
      },
      fontFamily: 'JetBrains Mono, Menlo, Monaco, "Courier New", monospace',
      fontSize: 13,
      lineHeight: 1.4
    });
    const fitAddon = new FitAddon();
    term.loadAddon(fitAddon);
    term.loadAddon(new WebLinksAddon());
    term.open(terminalRef.current);
    setTimeout(() => { try { fitAddon.fit(); } catch (_) {} }, 100);
    xtermRef.current = term;
    fitAddonRef.current = fitAddon;
    term.writeln('\x1b[38;5;81mConnecting to workspace terminal...\x1b[0m\r\n');

    try {
      const data = await api.createTerminalSession(appName, owner || null, {
        mode: terminalMode,
        command: '/bin/sh',
        workDir: sshInfo?.basePath || (deploymentType === 'compose' ? '/app' : '/home/app')
      });
      const wsUrl = api.getTerminalWsUrl(data.sessionId);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      termRetryRef.current = 0;

      ws.onopen = () => {
        term.writeln('\x1b[32mConnected successfully!\x1b[0m\r\n');
        ws.send(JSON.stringify({ type: 'resize', cols: term.cols || 80, rows: term.rows || 24 }));
        if (autoDeploy && deploymentType === 'compose') {
          term.writeln('\x1b[36m[Auto-Deploy] Waiting for Docker daemon to initialize, then starting docker-compose...\x1b[0m\r\n');
          ws.send(JSON.stringify({ 
            type: 'input', 
            data: 'echo "Waiting for Docker daemon..."; while ! docker info > /dev/null 2>&1; do sleep 1; done; echo "Docker ready!"; docker-compose up -d --build\r' 
          }));
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'output' || msg.type === 'data') {
            term.write(msg.data);
          } else if (msg.type === 'error') {
            term.writeln(`\r\n\x1b[31m${msg.message}\x1b[0m\r\n`);
          } else if (msg.type === 'exit') {
            term.writeln(`\r\n\x1b[33m[session exited: ${msg.code}]\x1b[0m\r\n`);
          }
        } catch (_) {}
      };

      ws.onclose = () => {
        term.writeln('\r\n\x1b[33m[disconnected] Press Reconnect or switch to Terminal again.\x1b[0m\r\n');
      };

      term.onData(data => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'input', data }));
        }
      });

      term.onResize(({ cols, rows }) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'resize', cols, rows }));
        }
      });

      const resizeHandler = () => {
        if (fitAddonRef.current) {
          try { fitAddonRef.current.fit(); } catch (_) {}
        }
      };
      window.addEventListener('resize', resizeHandler);
      // Store handler reference on the terminal instance for cleanup
      term._resizeHandler = resizeHandler;
    } catch (err) {
      const code = err.code || '';
      term.writeln(`\x1b[31mConnection failed${code ? ` [${code}]` : ''}: ${err.message}\x1b[0m\r\n`);
      // Retry a few times while the container is still coming up.
      if (termRetryRef.current < 8 && (code === 'APP_NOT_FOUND' || code === 'CONTAINER_NOT_RUNNING' || /not found|not running/i.test(err.message || ''))) {
        termRetryRef.current += 1;
        const waitSec = Math.min(2 + termRetryRef.current, 8);
        term.writeln(`\x1b[33mRetrying in ${waitSec}s (attempt ${termRetryRef.current}/8)...\x1b[0m\r\n`);
        setTimeout(() => initTerminal({ force: true }), waitSec * 1000);
      } else {
        term.writeln('\x1b[90mUse the Reconnect button when the container is ready.\x1b[0m\r\n');
      }
    }
  };

  const reconnectTerminal = () => {
    termRetryRef.current = 0;
    setActiveTab('terminal');
    initTerminal({ force: true });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const targetDir = joinPath(baseDir, currentDir);
      await api.uploadZipToContainer(appName, file, owner, targetDir, (progress) => {
        setUploadProgress(progress);
      });
      await loadFiles();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleDownload = async (file) => {
    setDownloadingFile(file.path);
    try {
      await api.downloadContainerFile(appName, file.path, owner);
    } catch (err) {
      alert('Download failed: ' + err.message);
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleExtract = async (filename) => {
    setExtracting(true);
    try {
      await api.extractContainerZip(appName, filename, joinPath(baseDir, currentDir), owner);
      await loadFiles();
    } catch (err) {
      alert('Extraction failed: ' + err.message);
    } finally {
      setExtracting(false);
    }
  };

  const triggerDeploy = () => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      alert('Terminal is not connected yet. Please wait a moment.');
      return;
    }
    setActiveTab('terminal');
    wsRef.current.send(JSON.stringify({ type: 'input', data: 'docker-compose up -d --build\r' }));
  };

  const runAiPrompt = async (promptText, { auto = false } = {}) => {
    const text = String(promptText || '').trim();
    if (!text) return;
    if (aiBusyRef.current) return;
    aiBusyRef.current = true;

    const userMsg = { role: 'user', content: text };
    setAiLoading(true);
    setCenterPane('activity');
    setRunVerification(null);
    setAiStatus(auto ? 'Checking whether the app is already running...' : 'Starting...');
    // New turn: cancel stuck RUNNING tool rows so they don't stay blue forever.
    // Keep prior chat; purple [AI] console reply is hidden while aiLoading.
    setAiChat(prev => [
      ...prev.map((m) => (m.role === 'log' && m.status === 'running'
        ? { ...m, status: 'done', ok: false, result: (m.result || '') || 'cancelled (new request)' }
        : m)),
      // Hide internal system prompts from the user — only show explicit user messages.
      ...(auto ? [] : [userMsg])
    ]);

    const abortController = new AbortController();
    aiAbortControllerRef.current = abortController;

    try {
      // Intelligence Fix: Include tool result summaries in history so the agent remembers
      // what it already read/ran. Convert log entries into condensed assistant messages.
      // Increased from 1200→2500 chars to preserve more context.
      const history = [];
      for (const m of aiChat) {
        if (m.role === 'user' || m.role === 'assistant') {
          history.push({ role: m.role, content: m.content });
        } else if (m.role === 'log' && m.status === 'done' && m.result) {
          // Condense tool results into assistant context messages
          const toolSummary = `[${m.tool}${m.detail ? ': ' + m.detail : ''}] ${m.ok ? 'OK' : 'FAILED'}\n${String(m.result).slice(0, 2500)}`;
          history.push({ role: 'assistant', content: toolSummary });
        }
      }

        const data = await api.workspaceAgent(appName, userMsg.content, history, {
        isAuto: auto,
        mode: agentMode,
        workDir: baseDir || sshInfo?.basePath,
        signal: abortController.signal,
        onEvent: (event, payload) => {
          if (event === 'status') {
            setAiStatus(payload.message || 'Working...');
            setAiThinking('');
          } else if (event === 'status_stream') {
            setAiThinking(prev => prev + (payload.chunk || ''));
          } else if (event === 'tool_start') {
            setAiThinking('');
            setAiStatus(`Running ${payload.tool}${payload.detail ? `: ${payload.detail}` : ''}`);
            setAiChat(prev => [
              ...prev,
              {
                role: 'log',
                id: payload.id,
                index: payload.index,
                tool: payload.tool,
                detail: payload.detail || '',
                args: payload.args || {},
                result: '',
                status: 'running',
                ok: null,
                startedAt: Date.now()
              }
            ]);
          } else if (event === 'tool_result') {
            setAiChat(prev => {
              const next = [...prev];
              const idx = next.findIndex(m => m.role === 'log' && m.id === payload.id);
              const prevEntry = idx >= 0 ? next[idx] : {};
              const startedAt = prevEntry.startedAt || Date.now();
              const durationMs = Date.now() - startedAt;
              const entry = {
                ...prevEntry,
                role: 'log',
                id: payload.id,
                index: payload.index,
                tool: payload.tool,
                detail: payload.detail || '',
                args: payload.args || {},
                result: payload.result || '',
                status: 'done',
                ok: payload.ok !== false,
                durationMs
              };
              if (idx >= 0) next[idx] = entry;
              else next.push(entry);
              return next;
            });
          } else if (event === 'thinking') {
            const thinkText = (payload.content || '').trim();
            if (thinkText) {
              setAiStatus('Thinking...');
              setAiChat(prev => {
                // Replace the last thinking bubble if it exists (avoid clutter)
                const lastIdx = prev.length - 1;
                if (lastIdx >= 0 && prev[lastIdx].role === 'thinking') {
                  const next = [...prev];
                  next[lastIdx] = { role: 'thinking', content: thinkText };
                  return next;
                }
                return [...prev, { role: 'thinking', content: thinkText }];
              });
            }
          } else if (event === 'done' && payload && payload.verification) {
            if (!payload.verification.skipped) {
              setRunVerification(payload.verification);
              finishIfVerified(payload.verification);
            }
          }
        }
      });

      if (data && data.verification && !data.verification.skipped) {
        setRunVerification(data.verification);
        finishIfVerified(data.verification);
      }
      setAiChat(prev => [...prev, { role: 'assistant', content: data.reply || "I'm sorry, I couldn't process that." }]);
      if (auto) setAiAutoDone(true);
      // Stay on activity so logs remain visible; user can open Preview manually.
    } catch (err) {
      if (err.name === 'AbortError' || err.message.includes('aborted')) {
        setAiChat(prev => [...prev, { role: 'assistant', content: '⚠️ AI execution stopped.' }]);
      } else {
        setAiChat(prev => [...prev, { role: 'assistant', content: `⚠️ **Deployment Error:**\n${err.message}\n\n*Tip: Try restarting your backend server to apply recent codebase updates.*` }]);
        setRunVerification({ ok: false, message: err.message || 'Verification did not run.' });
      }
    } finally {
      if (aiAbortControllerRef.current === abortController) {
        aiAbortControllerRef.current = null;
      }
      aiBusyRef.current = false;
      setAiLoading(false);
      setAiStatus('');
      setAiThinking('');
    }
  };

  const handleAiSubmit = async (e) => {
    e.preventDefault();
    if (!aiInput.trim()) return;
    const text = aiInput;
    setAiInput('');
    await runAiPrompt(text);
  };

  const handleStopAi = () => {
    if (aiAbortControllerRef.current) {
      aiAbortControllerRef.current.abort();
      aiAbortControllerRef.current = null;
    }
  };


  const statusClass = sshInfo?.isDeploying ? 'busy' : (sshInfo?.setupError ? 'warn' : '');
  const statusLabel = sshInfo?.isDeploying ? 'Provisioning' : (sshInfo?.setupError ? 'Manual setup' : 'Ready');
  const typeLabel = deploymentType === 'compose' ? 'Compose' : 'Dev workspace';

  const railBtn = (id, icon, label) => (
    <button
      type="button"
      className={`ws-rail-btn${activeTab === id ? ' active' : ''}`}
      onClick={() => setActiveTab(id)}
      title={label}
    >
      {icon}
      <span>{label}</span>
    </button>
  );

  const previewUrl = sshInfo?.domain
    ? `${window.location.protocol}//${sshInfo.domain}`
    : (sshInfo?.url || '');

  const aiLogs = aiChat.filter((m) => m.role === 'log');
  const processChatMessage = (text) => {
    let content = String(text || '')
      .replace(/<@CALL_TOOL[\s\S]*?>/gi, '')
      .replace(/<\/?CALL_TOOL[^>]*>/gi, '');
      
    const thoughts = [];
    const thoughtRegex = /<(?:thought|think)>([\s\S]*?)<\/(?:thought|think)>/gi;
    let match;
    while ((match = thoughtRegex.exec(content)) !== null) {
      if (match[1].trim()) thoughts.push(match[1].trim());
    }
    
    content = content.replace(/<(?:thought|think)>[\s\S]*?<\/(?:thought|think)>/gi, '').trim();
    
    return { content, thoughts };
  };

  const aiMessages = aiChat
    .filter((m) => m.role !== 'log' && m.role !== 'system' && m.role !== 'thinking')
    .map((m) => {
      if (m.role === 'assistant') {
        const { content, thoughts } = processChatMessage(m.content);
        return { ...m, content, thoughts, _processed: true };
      }
      return m;
    })
    .filter((m) => m.role !== 'assistant' || m.content || (m.thoughts && m.thoughts.length > 0));
  const latestAssistant = [...aiMessages].reverse().find((m) => m.role === 'assistant');

  const formatLogBlock = (msg) => {
    let status = msg.status === 'running' ? 'RUNNING' : (msg.ok === false ? 'FAILED' : 'DONE');
    if (msg.durationMs !== undefined) {
      status += ` (${(msg.durationMs / 1000).toFixed(1)}s)`;
    }
    const lines = [];
    lines.push(`[#${msg.index || '-'}] ${String(msg.tool || 'tool').toUpperCase()} — ${status}`);
    if (msg.detail) lines.push(`$ ${msg.detail}`);
    if (msg.result) lines.push(String(msg.result).replace(/\s+$/, ''));
    else if (msg.status === 'running') lines.push('... waiting for output');
    return lines.join('\n');
  };

  const conversationMessages = aiMessages.filter((m) => m.role === 'user' || m.role === 'assistant');
  const hasConversation = conversationMessages.length > 0;

  const handleDownloadLogs = () => {
    let logContent = `--- Workspace Logs for ${appName} ---\n`;
    logContent += `Started At: ${new Date(startedAtRef.current).toISOString()}\n`;
    logContent += `Type: ${deploymentType}\n\n`;

    if (deploymentSession?.logs && deploymentSession.logs.length > 0) {
      logContent += `--- Deployment Logs ---\n`;
      deploymentSession.logs.forEach(log => {
        logContent += `[${new Date(log.ts).toLocaleTimeString()}] [${log.level.toUpperCase()}] ${log.message}\n`;
      });
      logContent += `\n`;
    }

    if (aiChat && aiChat.length > 1) {
      logContent += `--- AI Chat Logs ---\n`;
      aiChat.forEach((msg) => {
        if (msg.role === 'system') return;
        if (msg.role === 'log') {
          logContent += `[Tool Output - ${msg.tool}] ${msg.detail || ''}\n`;
          if (msg.result) {
            logContent += `Result:\n${String(msg.result)}\n`;
          }
        } else {
          logContent += `[${msg.role.toUpperCase()}] ${msg.content}\n`;
        }
      });
    }

    const blob = new Blob([logContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${appName}-workspace-logs.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <>
      <div className="gws-root" style={isMinimized ? { display: 'none' } : {}}>
      <div className="gws-backdrop" onClick={() => (sshInfo?.isDeploying && onMinimize ? onMinimize() : null)} />
      <div className="gws-modal" role="dialog" aria-label="Workspace AI">
        <header className="gws-modal-header">
          <button type="button" className="gws-modal-title" title={appName}>
            <span className="gws-title-dot" />
            <span>Workspace AI: <strong>{appName}</strong></span>
            <ChevronDown size={14} />
          </button>

          <nav className="gws-tabs">
            <button
              type="button"
              className={`gws-tab ${activeTab === 'ai' ? 'is-active' : ''}`}
              onClick={() => setActiveTab('ai')}
            >
              <Bot size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              AI Assistant
            </button>
          </nav>

          <div className="gws-modal-actions">
            <button type="button" className="gws-minimize-btn" style={{ marginRight: '8px', display: 'flex', alignItems: 'center', gap: '4px' }} onClick={handleDownloadLogs} title="Download Logs">
              <Download size={14} /> Logs
            </button>
            {onMinimize && (
              <button type="button" className="gws-minimize-btn" onClick={onMinimize}>Minimize</button>
            )}
            <button
              type="button"
              className="gws-close-btn"
              title="Close"
              onClick={() => {
                const isWarningConfirmed = window.confirm(
                  sshInfo?.isDeploying
                    ? 'Deployment is still in progress. Closing this workspace will cancel the deployment. Are you sure you want to close and cancel?'
                    : 'Are you sure you want to close the AI Workspace? Any ongoing AI tasks may be interrupted.'
                );
                if (isWarningConfirmed) {
                  if (sshInfo?.isDeploying && onCancel) {
                    onCancel();
                  } else {
                    onClose();
                  }
                } else if (sshInfo?.isDeploying && onMinimize) {
                  onMinimize();
                }
              }}
            >
              <X size={20} />
            </button>
          </div>
        </header>

        <div className="gws-modal-body">
          <div className="gws-chat-pane" style={activeTab === 'ai' ? {} : { display: 'none' }}>
              {sshInfo?.isDeploying && (
                <div className="gws-provision">
                  <div className="gws-provision-title">
                    <LoaderCircle className="spinning" size={16} color="#a5b4fc" />
                    <span>Provisioning Workspace Container...</span>
                  </div>
                  <ul className="gws-provision-list">
                    {(deploymentSession?.milestones || []).map((m, i) => (
                      <li key={m.key || i} className={`gws-provision-item is-${m.status || 'pending'}`}>
                        <span className="gws-provision-icon">
                          {m.status === 'done' || m.status === 'completed' ? (
                            <Check size={14} color="#34d399" />
                          ) : m.status === 'running' ? (
                            <LoaderCircle size={14} className="spinning" color="#a5b4fc" />
                          ) : (
                            <span className="gws-provision-ring" />
                          )}
                        </span>
                        <span>{m.label}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="gws-provision-foot">The workspace will be ready for you to manually inspect soon.</p>
                </div>
              )}

              {!hasConversation && !aiLoading && !sshInfo?.isDeploying && (
                <div className="gws-chat-empty" style={{ marginTop: '2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                  <div className="gws-chat-empty-mark" aria-hidden="true" style={{ marginBottom: '8px', color: '#e4e4e7' }}><Sparkles size={28} /></div>
                  <strong style={{ fontSize: '1.25rem', color: '#e4e4e7' }}>Ask Workspace AI anything</strong>
                  <span style={{ color: '#9aa0a6', maxWidth: '300px', lineHeight: '1.4' }}>Install, start, or fix your app. Tools run inside the container.</span>
                </div>
              )}



              {aiChat.map((msg, i) => {
                if (msg.role === 'system') return null;

                if (msg.role === 'log') {
                  const isRunning = msg.status === 'running';
                  const isOk = msg.ok !== false;
                  const hasOutput = Boolean(msg.result && !isRunning);

                  if (hasOutput) {
                    return (
                      <details
                        key={`step-${msg.id || msg.index || i}-${msg.tool}`}
                        className={`ws-inline-log is-expandable ${isRunning ? 'is-running' : isOk ? 'is-ok' : 'is-fail'}`}
                      >
                        <summary className="ws-inline-log-head">
                          <div className="ws-inline-log-main">
                            <span className={`ws-log-status-badge ${isOk ? 'is-ok' : 'is-fail'}`}>
                              {isOk ? <Check size={11} strokeWidth={2.5} /> : <AlertTriangle size={11} />}
                            </span>
                            <span className="ws-log-pill">{msg.tool}</span>
                            <span className="ws-log-text" title={msg.detail}>{msg.detail}</span>
                            {msg.durationMs !== undefined && (
                              <span className="ws-log-duration">
                                {(msg.durationMs / 1000).toFixed(1)}s
                              </span>
                            )}
                          </div>
                          <span className="ws-log-output-btn">
                            <TerminalIcon size={11} />
                            <span>Output</span>
                            <ChevronDown size={11} className="ws-log-chevron" />
                          </span>
                        </summary>
                        <div className="ws-log-drawer">
                          <pre className="ws-log-pre">{String(msg.result).slice(0, 3000)}</pre>
                        </div>
                      </details>
                    );
                  }

                  return (
                    <div
                      key={`step-${msg.id || msg.index || i}-${msg.tool}`}
                      className={`ws-inline-log ${isRunning ? 'is-running' : isOk ? 'is-ok' : 'is-fail'}`}
                    >
                      <div className="ws-inline-log-head">
                        <div className="ws-inline-log-main">
                          <span className={`ws-log-status-badge ${isRunning ? 'is-running' : isOk ? 'is-ok' : 'is-fail'}`}>
                            {isRunning ? (
                              <span className="ws-log-spinner" aria-hidden="true" />
                            ) : isOk ? (
                              <Check size={11} strokeWidth={2.5} />
                            ) : (
                              <AlertTriangle size={11} />
                            )}
                          </span>
                          <span className="ws-log-pill">{msg.tool}</span>
                          <span className="ws-log-text" title={msg.detail}>{msg.detail}</span>
                          {msg.durationMs !== undefined && (
                            <span className="ws-log-duration">
                              {(msg.durationMs / 1000).toFixed(1)}s
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                if (msg.role === 'thinking') {
                  return (
                    <div key={`thinking-${i}`} className="ws-inline-thoughts" style={{ margin: '6px 0' }}>
                      <details className="ws-inline-thought-details" open>
                        <summary className="ws-inline-thought-summary">
                          <Lightbulb size={14} />
                          <span>Agent Thinking</span>
                          <ChevronRight size={12} className="ws-thought-chevron" />
                        </summary>
                        <div className="ws-inline-thought-content">{msg.content}</div>
                      </details>
                    </div>
                  );
                }

                if (msg.role === 'user') {
                  return (
                    <div key={`user-${i}`} className="gws-msg is-user">
                      <div className="gws-user-bubble">{msg.content}</div>
                    </div>
                  );
                }

                if (msg.role === 'assistant') {
                  // Use pre-processed content/thoughts if available (avoid double processing)
                  const content = msg._processed ? msg.content : processChatMessage(msg.content).content;
                  const thoughts = msg._processed ? msg.thoughts : processChatMessage(msg.content).thoughts;
                  if (!content && !(thoughts && thoughts.length)) return null;
                  return (
                    <div key={`assistant-${i}-${(content || '').slice(0,20)}`} className="gws-msg is-assistant">
                      <div className="gws-assistant-body">
                        {thoughts && thoughts.length > 0 && (
                          <div className="ws-inline-thoughts" style={{ marginBottom: '16px' }}>
                            {thoughts.map((thought, tidx) => (
                              <details key={tidx} className="ws-inline-thought-details" open={tidx === thoughts.length - 1}>
                                <summary className="ws-inline-thought-summary">
                                  <Lightbulb size={14} />
                                  <span>Thought</span>
                                  <ChevronRight size={12} className="ws-thought-chevron" />
                                </summary>
                                <div className="ws-inline-thought-content">{thought}</div>
                              </details>
                            ))}
                          </div>
                        )}
                        {content && (
                          <div className="ws-ai-markdown">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return null;
              })}

              {aiThinking && (
                <div className="ws-msg is-assistant">
                  <div className="ws-msg-avatar"><Bot size={18} /></div>
                  <div className="ws-msg-bubble" style={{ width: '100%' }}>
                    <div className="ws-inline-thoughts ws-active-thought" style={{ margin: '6px 0' }}>
                      <details className="ws-inline-thought-details" open>
                        <summary className="ws-inline-thought-summary">
                          <LoaderCircle size={14} className="spinning" style={{ color: '#2496ed' }} />
                          <span style={{ marginLeft: '8px', color: '#fff', fontWeight: 500 }}>Thinking...</span>
                          <ChevronRight size={12} className="ws-thought-chevron" style={{ marginLeft: 'auto' }} />
                        </summary>
                        <div className="ws-inline-thought-content" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{aiThinking + '▌'}</ReactMarkdown>
                          <div ref={thinkingRef} />
                        </div>
                      </details>
                    </div>
                  </div>
                </div>
              )}

              {aiLoading && !aiThinking && (
                <div className="gws-typing">
                  <LoaderCircle size={14} className="spinning" />
                  {aiStatus ? (aiStatus.includes(':') ? aiStatus.split(':')[0] : aiStatus) : 'Thinking…'}
                </div>
              )}
              <div ref={chatEndRef} className="gws-chat-end" />
            </div>

          {/* Terminal tab view */}
          <div className={`gws-terminal-pane ${activeTab === 'terminal' ? '' : 'is-hidden'}`}>
            <div ref={terminalRef} className="gws-terminal" />
          </div>

          {/* Files & Code Editor Tab */}
          <div className="gws-files-pane" style={activeTab === 'files' ? { display: 'flex' } : { display: 'none' }}>
            <div className="gws-files-sidebar">
              <div className="gws-files-sidebar-header">
                <div className="gws-files-path" title={`${baseDir || '/home/app'}${currentDir ? `/${currentDir}` : ''}`}>
                  <Folder size={12} style={{ marginRight: '4px', verticalAlign: 'middle', color: '#818cf8' }} />
                  {currentDir ? `/${currentDir}` : (baseDir || '/home/app')}
                </div>
                <div className="gws-files-sidebar-actions">
                  {currentDir && (
                    <button
                      type="button"
                      className="gws-tool-btn"
                      style={{ width: '26px', height: '26px' }}
                      title="Parent Directory"
                      onClick={() => {
                        const p = parentDir(currentDir);
                        setCurrentDir(p);
                        loadFiles(p);
                      }}
                    >
                      <ArrowLeft size={13} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="gws-tool-btn"
                    style={{ width: '26px', height: '26px' }}
                    title="Refresh Files"
                    onClick={() => loadFiles(currentDir)}
                    disabled={loadingFiles}
                  >
                    <RefreshCw size={13} className={loadingFiles ? 'spinning' : ''} />
                  </button>
                </div>
              </div>

              <div className="gws-files-list">
                {loadingFiles && files.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                    <LoaderCircle className="spinning" size={18} style={{ margin: '0 auto 8px', display: 'block' }} />
                    Loading files...
                  </div>
                ) : filesError ? (
                  <div style={{ padding: '16px', color: '#f87171', fontSize: '12px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 8px' }}>{filesError}</p>
                    <button
                      type="button"
                      className="gws-save-btn"
                      style={{ padding: '3px 10px', fontSize: '11px', background: '#374151' }}
                      onClick={() => loadFiles(currentDir)}
                    >
                      Retry
                    </button>
                  </div>
                ) : files.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '12px' }}>
                    No files found in directory
                  </div>
                ) : (
                  files.map((entry, idx) => {
                    const isDir = entry.isDir;
                    const isSelected = activeFile?.path === entry.path;
                    return (
                      <div
                        key={entry.path || entry.name || idx}
                        className={`gws-file-item ${isDir ? 'is-dir' : 'is-file'} ${isSelected ? 'is-active' : ''}`}
                        onClick={() => {
                          if (isDir) {
                            const nextDir = currentDir ? `${currentDir}/${entry.name}` : entry.name;
                            setCurrentDir(nextDir);
                            loadFiles(nextDir);
                          } else {
                            openFileEditor(entry);
                          }
                        }}
                      >
                        {isDir ? (
                          <Folder size={14} color="#818cf8" style={{ flexShrink: 0 }} />
                        ) : (
                          <FileText
                            size={14}
                            color={
                              entry.name.endsWith('.js') || entry.name.endsWith('.ts') || entry.name.endsWith('.jsx') || entry.name.endsWith('.tsx')
                                ? '#38bdf8'
                                : entry.name.endsWith('.json')
                                ? '#facc15'
                                : entry.name.endsWith('.py')
                                ? '#34d399'
                                : entry.name.endsWith('.css') || entry.name.endsWith('.html')
                                ? '#f472b6'
                                : '#94a3b8'
                            }
                            style={{ flexShrink: 0 }}
                          />
                        )}
                        <span className="gws-file-name" title={entry.name}>
                          {entry.name}
                        </span>
                        {!isDir && entry.size && (
                          <span className="gws-file-size">{formatFileSize(entry.size)}</span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="gws-editor-container">
              {!activeFile ? (
                <div className="gws-editor-empty">
                  <Code size={44} style={{ opacity: 0.25 }} />
                  <h3>Select a file to inspect or edit</h3>
                  <p>Browse the project directory on the left and click any file to open the code editor.</p>
                </div>
              ) : loadingFileContent ? (
                <div className="gws-editor-empty">
                  <LoaderCircle size={28} className="spinning" style={{ color: '#818cf8' }} />
                  <p style={{ marginTop: '8px' }}>Loading file content...</p>
                </div>
              ) : (
                <>
                  <div className="gws-editor-header">
                    <div className="gws-editor-file-info">
                      <FileCode size={16} color="#38bdf8" />
                      <strong>{activeFile.name}</strong>
                      <span className="path">{activeFile.path}</span>
                    </div>
                    <div className="gws-editor-actions">
                      <button
                        type="button"
                        className="gws-save-btn"
                        onClick={saveActiveFile}
                        disabled={savingFileContent}
                      >
                        <Save size={13} />
                        {savingFileContent ? 'Saving...' : 'Save File'}
                      </button>
                      <button
                        type="button"
                        className="gws-tool-btn"
                        style={{ width: '28px', height: '28px' }}
                        title="Close File"
                        onClick={() => setActiveFile(null)}
                      >
                        <X size={15} />
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="gws-editor-textarea"
                    value={fileContent}
                    onChange={(e) => setFileContent(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        e.preventDefault();
                        const start = e.target.selectionStart;
                        const end = e.target.selectionEnd;
                        const nextVal = fileContent.substring(0, start) + '  ' + fileContent.substring(end);
                        setFileContent(nextVal);
                        setTimeout(() => {
                          e.target.selectionStart = e.target.selectionEnd = start + 2;
                        }, 0);
                      }
                    }}
                    spellCheck="false"
                    placeholder="Empty file"
                  />
                  <div className="gws-editor-footer">
                    <span>{fileContent.split('\n').length} lines • {fileContent.length} chars</span>
                    <span>{activeFile.path}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Environment Variables Tab */}
          <div className="gws-env-pane" style={activeTab === 'env' ? { display: 'flex' } : { display: 'none' }}>
            <div className="gws-env-header">
              <div>
                <h3>Environment Configuration (.env)</h3>
                <p>Values are mounted into the runtime container</p>
              </div>
              <button
                type="button"
                className="gws-save-btn"
                onClick={saveEnv}
                disabled={savingEnv || loadingEnv}
              >
                <Save size={13} />
                {savingEnv ? 'Saving...' : 'Save Environment'}
              </button>
            </div>
            {loadingEnv ? (
              <div className="gws-editor-empty">
                <LoaderCircle size={28} className="spinning" style={{ color: '#818cf8' }} />
                <p style={{ marginTop: '8px' }}>Loading .env file...</p>
              </div>
            ) : (
              <textarea
                className="gws-env-textarea"
                value={envContent}
                onChange={(e) => setEnvContent(e.target.value)}
                placeholder="# Add environment variables here (KEY=VALUE)"
                spellCheck="false"
              />
            )}
          </div>
        </div>

        <div className="gws-composer-wrap" style={activeTab === 'ai' ? {} : { display: 'none' }}>
          <form className="gws-composer" onSubmit={handleAiSubmit}>
            <textarea
              className="gws-input"
              placeholder="Ask AI anything"
              value={aiInput}
              rows={2}
              onChange={(e) => {
                setAiInput(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (aiInput.trim()) handleAiSubmit(e);
                }
              }}
              disabled={aiLoading}
            />

            <div className="gws-toolbar">
              <div className="gws-toolbar-left">
                <button type="button" className="gws-tool-btn" title="Attach" onClick={() => fileInputRef.current?.click()}>
                  <Paperclip size={16} />
                </button>
                <button type="button" className="gws-tool-btn" title="Quote" aria-label="Quote">
                  <FileText size={16} />
                </button>

              </div>
              <div className="gws-toolbar-right">
                {aiLoading ? (
                  <button type="button" className="gws-send is-stop" onClick={handleStopAi} title="Stop">
                    <Square size={14} fill="currentColor" />
                  </button>
                ) : (
                  <button type="submit" className={`gws-send ${aiInput.trim() ? 'active' : ''}`} disabled={!aiInput.trim()} title="Send">
                    <ArrowUp size={18} />
                  </button>
                )}
              </div>
            </div>
          </form>
          <div className="gws-disclaimer">AI agents may make mistakes. Always verify configuration changes.</div>
        </div>
      </div>
      </div>
      {isMinimized && (
        <div 
          onClick={onRestore}
          style={{ 
            position: 'fixed', 
            bottom: '24px', 
            right: '24px', 
            zIndex: 99999, 
            background: 'linear-gradient(145deg, #18181b 0%, #09090b 100%)', 
            border: '1px solid rgba(255,255,255,0.1)', 
            padding: '12px 20px', 
            borderRadius: '12px', 
            color: '#fff', 
            cursor: 'pointer', 
            boxShadow: '0 10px 40px rgba(0,0,0,0.6)', 
            display: 'flex', 
            alignItems: 'center', 
            gap: '14px',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 15px 45px rgba(0,0,0,0.8)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 10px 40px rgba(0,0,0,0.6)'; }}
        >
          <div style={{ position: 'relative', width: 12, height: 12 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: sshInfo?.isDeploying ? '#fbbf24' : 'var(--primary)', opacity: 0.4, animation: 'ws-orb-pulse 1.5s infinite' }} />
            <div style={{ position: 'absolute', inset: 2, borderRadius: '50%', background: sshInfo?.isDeploying ? '#fbbf24' : 'var(--primary)' }} />
          </div>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '13px', letterSpacing: '0.02em', color: '#f8fafc' }}>Workspace AI: {appName}</div>
            <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{sshInfo?.isDeploying ? 'Deployment in progress...' : 'Active and listening'}</div>
          </div>
          <Maximize2 size={16} color="#94a3b8" style={{ marginLeft: '12px' }} />
        </div>
      )}
    </>,
    document.body
  );
};

export default ComposeWorkspaceModal;
